import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { X, Check, RotateCcw, Sparkles, Palette, Loader2 } from 'lucide-react';
import { AVATAR_ASSETS, SKIN_TONES, HAIR_COLORS, CLOTHING_COLORS } from '../constants/AvatarAssets';
import AvatarRenderer, { AvatarConfig } from './AvatarRenderer';
import { useTheme } from '../contexts/ThemeContext';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    initialConfig?: AvatarConfig | null;
    onSave: (config: AvatarConfig) => Promise<void>;
}

const CATEGORIES = [
    { id: 'bodies', name: 'Pele', icon: '👤' },
    { id: 'hair', name: 'Cabelo', icon: '💇' },
    { id: 'face', name: 'Rosto', icon: '👀' },
    { id: 'clothing', name: 'Roupa', icon: '👕' },
    { id: 'accessories', name: 'Acessórios', icon: '👓' },
    { id: 'backgrounds', name: 'Fundo', icon: '🖼️' },
];

const AvatarCreatorModal: React.FC<Props> = ({ isOpen, onClose, initialConfig, onSave }) => {
    const { accentColor } = useTheme();
    const { t } = useTranslation();
    const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].id);
    const [config, setConfig] = useState<AvatarConfig>(() => initialConfig || {
        background: 'none',
        body: 'light',
        skinTone: SKIN_TONES[0],
        face: 'normal',
        detail: 'none',
        clothing: 'basic',
        clothingColor: CLOTHING_COLORS[0],
        hair: 'normal',
        hairColor: HAIR_COLORS[0],
        accessory: 'none',
        flag: 'none',
    });

    const [isSaving, setIsSaving] = useState(false);

    if (!isOpen) return null;

    const updateConfig = (key: keyof AvatarConfig, value: string) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(config);
            onClose();
        } catch (err) {
            console.error("Error saving avatar:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const currentAssets = (AVATAR_ASSETS as any)[activeCategory] || [];

    return (
        <div className="fixed inset-0 w-screen h-screen z-[200] flex items-center justify-center p-4 md:p-6 bg-black/30 backdrop-blur-xl animate-in fade-in duration-300" style={{ left: 0, top: 0 }}>
            <div className="bg-surface rounded-[28px] border border-white/20 w-full max-w-6xl h-[85vh] flex overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Lateral Navigation (Tabs) */}
                <div className="w-20 md:w-28 bg-transparent border-r border-black/[0.06] dark:border-white/[0.06] flex flex-col items-center py-8 gap-6 overflow-y-auto no-scrollbar">
                    <div className="mb-4">
                        <div className="w-12 h-12 bg-ink rounded-2xl flex items-center justify-center text-surface">
                            <Sparkles size={24} />
                        </div>
                    </div>
                    {CATEGORIES.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`
                                w-14 h-14 md:w-16 md:h-16 rounded-2xl flex flex-col items-center justify-center gap-1 transition-all
                                ${activeCategory === cat.id
                                    ? 'bg-ink text-surface scale-105'
                                    : 'text-muted hover:bg-tint-stone '}
                            `}
                        >
                            <span className="text-xl md:text-2xl">{cat.icon}</span>
                            <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em]">{cat.name}</span>
                        </button>
                    ))}
                    <button onClick={onClose} className="mt-auto w-12 h-12 rounded-full flex items-center justify-center text-muted hover:bg-tint-rose hover:text-bad transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                    {/* Left: Customization Options */}
                    <div className="flex-1 flex flex-col bg-surface overflow-hidden">
                        <div className="p-8 border-b border-black/[0.06] dark:border-white/[0.06] flex justify-between items-center">
                            <div>
                                <h3 className="text-2xl font-medium dark:text-ink tracking-[-0.03em]">{t('avatar.personalize')}</h3>
                                <p className="text-[10px] font-normal text-primary uppercase font-mono tracking-[0.14em] mt-1">{t('avatar.pixelArtStyle')}</p>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                            {/* Sub-categories or Color Pickers */}
                            {(activeCategory === 'bodies' || activeCategory === 'hair' || activeCategory === 'clothing') && (
                                <div className="mb-10">
                                    <h4 className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em] mb-4 flex items-center gap-2">
                                        <Palette size={12} /> Paleta de Cores
                                    </h4>
                                    <div className="flex flex-wrap gap-4">
                                        {(activeCategory === 'bodies' ? SKIN_TONES : activeCategory === 'hair' ? HAIR_COLORS : CLOTHING_COLORS).map(color => (
                                            <button
                                                key={color}
                                                onClick={() => updateConfig(activeCategory === 'bodies' ? 'skinTone' : activeCategory === 'hair' ? 'hairColor' : 'clothingColor', color)}
                                                className={`
                                                    w-10 h-10 rounded-full border-4 transition-all hover:scale-110
                                                    ${(activeCategory === 'bodies' ? config.skinTone : activeCategory === 'hair' ? config.hairColor : config.clothingColor) === color
                                                        ? 'border-primary ring-4 ring-primary/20'
                                                        : 'border-black/[0.06] dark:border-white/[0.06] dark:border-white/[0.06]'}
                                                `}
                                                style={{ backgroundColor: color }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Detail Sub-Category (Cleft lip, freckles) within 'Rosto' logic or separate */}
                            {activeCategory === 'face' && (
                                <div className="mb-10">
                                    <h4 className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em] mb-4">{t('avatar.marksAndFeatures')}</h4>
                                    <div className="flex gap-4">
                                        {AVATAR_ASSETS.details.map(det => (
                                            <button
                                                key={det.id}
                                                onClick={() => updateConfig('detail', det.id)}
                                                className={`
                                                    px-4 py-3 rounded-2xl border font-medium text-[10px] transition-all
                                                    ${config.detail === det.id ? 'border-primary bg-primary/5 text-primary' : 'border-black/[0.06] dark:border-white/[0.06] text-muted'}
                                                `}
                                            >
                                                {det.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {currentAssets.map((asset: any) => {
                                    const mapping: any = {
                                        bodies: 'body',
                                        hair: 'hair',
                                        face: 'face',
                                        details: 'detail',
                                        clothing: 'clothing',
                                        accessories: 'accessory',
                                        backgrounds: 'background'
                                    };
                                    const configKey = mapping[activeCategory];
                                    const isSelected = (config as any)[configKey] === asset.id;

                                    return (
                                        <button
                                            key={asset.id}
                                            onClick={() => updateConfig(configKey, asset.id)}
                                            className={`
                                                aspect-square rounded-3xl border flex flex-col items-center justify-center p-3 transition-all relative group
                                                ${isSelected
                                                    ? 'border-primary bg-primary/5'
                                                    : 'border-black/[0.06] dark:border-white/[0.06] hover:border-primary/20 bg-tint-stone/50 '}
                                            `}
                                        >
                                            <div className="flex-1 w-full flex items-center justify-center mb-2">
                                                {asset.url ? (
                                                    <img
                                                        src={asset.url}
                                                        className="max-w-[80%] max-h-[80%] object-contain"
                                                        style={{ imageRendering: 'pixelated' }}
                                                        alt={asset.name}
                                                    />
                                                ) : activeCategory === 'flags' ? (
                                                    <span className="text-4xl">{asset.emoji || '❌'}</span>
                                                ) : (
                                                    <X size={24} className="text-muted/60" />
                                                )}
                                            </div>
                                            <span className={`text-[8px] font-normal uppercase font-mono tracking-[0.14em] text-center ${isSelected ? 'text-primary' : 'text-muted'}`}>
                                                {asset.name}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="p-8 border-t border-black/[0.06] dark:border-white/[0.06] flex gap-4">
                            <button
                                onClick={() => setConfig(initialConfig || config)}
                                className="px-6 h-14 bg-tint-stone/60 text-muted rounded-2xl hover:bg-tint-stone transition-all flex items-center justify-center"
                                title="Resetar"
                            >
                                <RotateCcw size={20} />
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="flex-1 h-14 bg-primary text-slate-900 rounded-2xl font-normal text-xs uppercase font-mono tracking-[0.14em] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
                            >
                                {isSaving ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />}
                                Salvar Personagem
                            </button>
                        </div>
                    </div>

                    {/* Right: Real-time {t('avatar.livePreview')} */}
                    <div className="hidden md:flex w-96 bg-transparent p-10 flex-col items-center justify-center relative">
                        <div className="absolute top-10 text-center">
                            <h4 className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">{t('avatar.livePreview')}</h4>
                        </div>

                        <div className="relative">
                            <div className="absolute -inset-10 bg-primary/10 rounded-full blur-[60px]" />
                            <AvatarRenderer
                                config={config}
                                size={280}
                                className="relative z-10 shadow-full border-8 border-black/[0.06] dark:border-white/[0.06] dark:border-white/[0.06] ring-1 ring-black/10"
                            />
                        </div>

                        <div className="mt-12 w-full space-y-4">
                            <div className="p-4 bg-surface rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
                                <p className="text-[9px] font-normal text-muted uppercase font-mono tracking-[0.14em] mb-2 italic">{t('avatar.generatedConfig')}</p>
                                <pre className="text-[9px] font-mono text-primary truncate">
                                    {JSON.stringify(config, null, 2)}
                                </pre>
                            </div>
                            <p className="text-[9px] text-center text-muted font-medium leading-relaxed">
                                Suas escolhas são salvas instantaneamente na nuvem e visíveis para toda a rede LibriTui.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AvatarCreatorModal;
